import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  Shield, FileText, Calendar,
  User, Hash, CheckCircle2, ExternalLink, Clock
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { patents } from '../data/mockData';
import { trackButtonClick } from '../utils/trackButtonClick';

// Patent Seal SVG Component
const PatentSeal = ({ isDark }) => (
  <div className="relative">
    <svg width="80" height="80" viewBox="0 0 80 80" className="drop-shadow-lg">
      <circle cx="40" cy="40" r="38" fill="none" stroke={isDark ? '#f59e0b' : '#d97706'} strokeWidth="2" strokeDasharray="4 2" />
      <circle cx="40" cy="40" r="32" fill="none" stroke={isDark ? '#f59e0b' : '#d97706'} strokeWidth="1.5" />
      <circle cx="40" cy="40" r="28" fill={isDark ? 'rgba(245,158,11,0.1)' : 'rgba(217,119,6,0.08)'} />
      {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => {
        const rad = (angle * Math.PI) / 180;
        const x1 = 40 + 28 * Math.cos(rad);
        const y1 = 40 + 28 * Math.sin(rad);
        const x2 = 40 + 35 * Math.cos(rad);
        const y2 = 40 + 35 * Math.sin(rad);
        return (
          <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} 
            stroke={isDark ? '#f59e0b' : '#d97706'} strokeWidth="1.5" />
        );
      })}
      <text x="40" y="36" textAnchor="middle" fill={isDark ? '#f59e0b' : '#d97706'} fontSize="10" fontWeight="bold" fontFamily="serif">
        US
      </text>
      <text x="40" y="50" textAnchor="middle" fill={isDark ? '#f59e0b' : '#d97706'} fontSize="8" fontWeight="bold" fontFamily="serif">
        PATENT
      </text>
    </svg>
  </div>
);

// Individual Patent Card
const PatentCard = ({ patent, isDark, text: themeText, index }) => {
  const [ref, inView] = useInView({ threshold: 0, triggerOnce: true });
  const accentColor = isDark ? 'text-amber-400' : 'text-amber-600';

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.15, ease: 'easeOut' }}
      className="w-full"
    >
      <div className={`relative group ${
        isDark 
          ? 'bg-gradient-to-br from-white/[0.04] to-white/[0.01] border border-white/10 hover:border-amber-500/30' 
          : 'bg-gradient-to-br from-white/90 to-amber-50/30 border border-amber-200/50 hover:border-amber-400/60 shadow-xl shadow-amber-500/5'
      } rounded-2xl overflow-hidden transition-all duration-500`}>
        
        {/* Top Accent Bar */}
        <div className="h-1 w-full bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600"></div>

        {/* Status Badge - Top Right */}
        <div className="absolute top-4 right-4 sm:top-5 sm:right-6 z-10">
          {(() => {
            const statusConfig = {
              Granted: {
                icon: CheckCircle2,
                classes: isDark
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 shadow-emerald-500/10'
                  : 'bg-emerald-50 text-emerald-700 border border-emerald-300 shadow-emerald-500/10'
              },
              Pending: {
                icon: Clock,
                classes: isDark
                  ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40 shadow-amber-500/10 animate-pulse'
                  : 'bg-amber-50 text-amber-700 border border-amber-300 shadow-amber-500/10'
              },
              Filed: {
                icon: FileText,
                classes: isDark
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500/40 shadow-blue-500/10'
                  : 'bg-blue-50 text-blue-700 border border-blue-300 shadow-blue-500/10'
              }
            };
            const { icon: StatusIcon, classes } = statusConfig[patent.status] || {
              icon: FileText,
              classes: isDark
                ? 'bg-gray-500/20 text-gray-400 border border-gray-500/40 shadow-gray-500/10'
                : 'bg-gray-50 text-gray-700 border border-gray-300 shadow-gray-500/10'
            };
            return (
              <div className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold tracking-wider uppercase shadow-lg ${classes}`}>
                <StatusIcon className="w-3.5 h-3.5" />
                {patent.status}
              </div>
            );
          })()}
        </div>

        {/* Patent Header */}
        <div className="p-6 sm:p-8">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Left: Seal */}
            <div className="flex flex-col items-center lg:items-start gap-3 flex-shrink-0">
              {patent.googlePatentsUrl ? (
                <a
                  href={patent.googlePatentsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="View patent on Google Patents"
                  title="View on Google Patents"
                  onClick={() => trackButtonClick({
                    section: 'patents',
                    label: 'View on Google Patents',
                    type: 'external_link',
                    detail: patent.title
                  })}
                  className={`block rounded-full transition-transform duration-300 hover:scale-110 ${
                    isDark ? 'hover:drop-shadow-[0_0_12px_rgba(245,158,11,0.5)]' : 'hover:drop-shadow-[0_0_12px_rgba(217,119,6,0.4)]'
                  }`}
                >
                  <PatentSeal isDark={isDark} />
                </a>
              ) : (
                <PatentSeal isDark={isDark} />
              )}
            </div>

            {/* Right: Patent Info */}
            <div className="flex-1 min-w-0">
              {/* Category Badge */}
              <div className="mb-3">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                  isDark ? 'bg-blue-500/15 text-blue-400 border border-blue-500/20' : 'bg-blue-50 text-blue-700 border border-blue-200'
                }`}>
                  <Shield className="w-3 h-3" />
                  {patent.category}
                </span>
              </div>

              {/* Title */}
              <h3 className={`text-xl sm:text-2xl lg:text-3xl font-bold ${themeText.primary} mb-4 leading-tight`}>
                {patent.title}
              </h3>

              {/* Patent Meta Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                <div className={`flex items-center gap-2.5 ${themeText.secondary} text-sm`}>
                  <User className={`w-4 h-4 ${accentColor} flex-shrink-0`} />
                  <span>
                    <strong className={themeText.primary}>Inventors:</strong>{' '}
                    {patent.inventors.map((inventor, i) => {
                      const match = inventor.match(/^(.*?)(\s*\+\s*\d+\s*Others?)$/i);
                      const name = match ? match[1].trim() : inventor;
                      const suffix = match ? match[2].trim() : '';
                      return (
                        <React.Fragment key={inventor}>
                          {i > 0 && ', '}
                          <span className={name === 'Rahul Singh' ? `font-bold ${accentColor}` : ''}>{name}</span>
                          {suffix && <span className={themeText.secondary}> {suffix}</span>}
                        </React.Fragment>
                      );
                    })}
                  </span>
                </div>
                <div className={`flex items-center gap-2.5 ${themeText.secondary} text-sm`}>
                  <Hash className={`w-4 h-4 ${accentColor} flex-shrink-0`} />
                  <span><strong className={themeText.primary}>Patent ID:</strong> {patent.patentId}</span>
                  {patent.googlePatentsUrl && (
                    <a
                      href={patent.googlePatentsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="View patent on Google Patents"
                      title="View on Google Patents"
                      onClick={() => trackButtonClick({
                        section: 'patents',
                        label: 'View on Google Patents',
                        type: 'external_link',
                        detail: patent.title
                      })}
                      className={`inline-flex ${accentColor} hover:opacity-70 transition-opacity flex-shrink-0`}
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
                <div className={`flex items-center gap-2.5 ${themeText.secondary} text-sm`}>
                  <FileText className={`w-4 h-4 ${accentColor} flex-shrink-0`} />
                  <span><strong className={themeText.primary}>Organization:</strong> {patent.assignee}</span>
                </div>
                <div className={`flex items-center gap-2.5 ${themeText.secondary} text-sm`}>
                  <Calendar className={`w-4 h-4 ${accentColor} flex-shrink-0`} />
                  <span><strong className={themeText.primary}>{patent.status === 'Granted' ? 'Granted On' : 'Filed On'}:</strong> {patent.dateOfPatent}</span>
                </div>
              </div>

              {/* Technologies */}
              <div className="flex flex-wrap gap-2">
                {patent.technologies.map((tech, i) => (
                  <span key={i} className={`px-3 py-1 rounded-full text-xs font-medium ${
                    isDark 
                      ? 'bg-amber-500/10 text-amber-300 border border-amber-500/20' 
                      : 'bg-amber-50 text-amber-800 border border-amber-200'
                  }`}>
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className={`mx-6 sm:mx-8 border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`}></div>

        {/* Key Innovations */}
        <div className={`mx-6 sm:mx-8 my-6 sm:my-8 rounded-xl p-5 ${
          isDark 
            ? 'bg-gradient-to-br from-amber-500/5 to-orange-500/5 border border-amber-500/10' 
            : 'bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200/50'
        }`}>
          <h4 className={`text-sm font-bold ${accentColor} uppercase tracking-wider mb-4 flex items-center gap-2`}>
            <CheckCircle2 className="w-4 h-4" />
            Key Innovations
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {patent.keyInnovations.map((innovation, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <div className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 bg-gradient-to-r from-amber-500 to-orange-500`}></div>
                <span className={`text-sm ${themeText.secondary} leading-relaxed`}>{innovation}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Subtle background glow on hover */}
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/0 to-orange-500/0 group-hover:from-amber-500/[0.02] group-hover:to-orange-500/[0.02] transition-all duration-500 rounded-2xl pointer-events-none"></div>
      </div>
    </motion.div>
  );
};

const PatentSection = () => {
  const { isDark, background, text } = useTheme();
  const [headerRef, headerInView] = useInView({ threshold: 0, triggerOnce: true });

  return (
    <section id="patents" className={`min-h-screen py-20 ${background.section} relative overflow-hidden transition-all duration-500`}>
      {/* Subtle Background Pattern */}
      <div className={`absolute inset-0 ${isDark ? 'opacity-[0.03]' : 'opacity-[0.05]'}`}>
        <svg width="100%" height="100%">
          <defs>
            <pattern id="patent-grid" width="60" height="60" patternUnits="userSpaceOnUse">
              <path d="M 60 0 L 0 0 0 60" fill="none" stroke="currentColor" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#patent-grid)" />
        </svg>
      </div>

      {/* Floating accent orbs */}
      <div className={`absolute top-20 right-10 w-72 h-72 ${isDark ? 'bg-amber-500/5' : 'bg-amber-200/20'} rounded-full blur-3xl pointer-events-none`}></div>
      <div className={`absolute bottom-20 left-10 w-64 h-64 ${isDark ? 'bg-orange-500/5' : 'bg-orange-200/15'} rounded-full blur-3xl pointer-events-none`}></div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-5xl mx-auto">
          {/* Section Header */}
          <motion.div
            ref={headerRef}
            initial={{ opacity: 0, y: 30 }}
            animate={headerInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className={`inline-block px-4 py-2 ${background.card} rounded-full ${isDark ? 'text-amber-400' : 'text-amber-600'} font-mono text-sm mb-6`}>
              &gt; patents --list
            </div>
            <h2 className={`text-5xl lg:text-7xl font-bold ${text.gradient} mb-6 leading-relaxed`}>
              Patent
            </h2>
            <p className={`${text.muted} text-lg max-w-2xl mx-auto`}>
              Where deep technical expertise meets original thinking — inventions that matter
            </p>
          </motion.div>

          {/* Patents Grid */}
          <div className={`${patents.length > 1 ? 'grid grid-cols-1 gap-8' : ''}`}>
            {patents.map((patent, index) => (
              <PatentCard 
                key={patent.id} 
                patent={patent} 
                isDark={isDark} 
                text={text} 
                index={index}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PatentSection;
